import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  TextField,
  Paper,
  LinearProgress,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardLayout from '../Layouts/DashboardLayout';
import { RankingService } from '../../heatmap/lib/ranking-service';
import { GooglePlacesService } from '../../heatmap/lib/dataforseo-places';
import HeatmapMap from './HeatmapMap';
import HeatmapPreviewMap from './HeatmapPreviewMap';
import GoogleAdsKeywordService from '../../services/GoogleAdsKeywordService';
import GMBService from '../../services/GMBService';
import ApiService from '../../services/api';
import { hasHeatmapAccess as checkHeatmapAccess } from '../../services/SubscriptionService';
import { API_BASE_URL } from '../../config/api';
import { getSession, getSelectedBusinessLocationId } from '../../utils/authUtils';

import logger from '../../utils/logger';
const HeatmapPage = () => {
  // DataForSEO credentials (can be in format "login:password" or separate env vars)
  const defaultApiKey = process.env.REACT_APP_GOOGLE_API_KEY || 
    `${process.env.REACT_APP_DATAFORSEO_LOGIN}:${process.env.REACT_APP_DATAFORSEO_PASSWORD}` || '';

  const [form, setForm] = useState({
    businessName: '',
    keyword: '',
    lat: '',
    lng: '',
    gridSize: 5,
    radiusKm: 2,
  });
  const [locationLoading, setLocationLoading] = useState(false);
  const [progress, setProgress] = useState({ pct: 0, msg: '' });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const autoRunTried = useRef(false);
  
  // Plan gating state
  const [hasHeatmapAccess, setHasHeatmapAccess] = useState(null); // null = loading
  const [currentPlan, setCurrentPlan] = useState('none');
  
  // Keyword suggestions state
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [showKeywordSuggestions, setShowKeywordSuggestions] = useState(false);
  const [businessData, setBusinessData] = useState(null);
  const keywordsFetchedRef = useRef(false);

  // Enable live API to use production Visibeen backend endpoint
  const rankingService = useMemo(() => new RankingService(defaultApiKey, true, true), [defaultApiKey]);
  const places = useMemo(() => new GooglePlacesService(defaultApiKey, true), [defaultApiKey]);

  const update = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));

  // Check plan for heatmap access - Basic cannot use heatmap
  useEffect(() => {
    const checkPlanAccess = async () => {
      try {
        // Get the selected business location ID
        const locationId = getSelectedBusinessLocationId();
        
        if (!locationId) {
          // No location selected, default to pro (allow access)
          setHasHeatmapAccess(true);
          setCurrentPlan('pro');
          return;
        }
        
        // Fetch plan from API (no caching to get real-time updates)
        const result = await ApiService.get(`/admin/gmb-account/plan?location_id=${encodeURIComponent(locationId)}&_t=${Date.now()}`);
        
        let plan = 'pro'; // Default to pro
        if (result?.success && result?.data?.plan) {
          plan = result.data.plan;
        } else if (result?.plan) {
          plan = result.plan;
        }
        
        setCurrentPlan(plan);
        // Basic plan cannot use heatmap
        setHasHeatmapAccess(checkHeatmapAccess(plan));
        
        logger.log(`[HeatmapPage] Plan: ${plan}, Heatmap Access: ${checkHeatmapAccess(plan)}`);
      } catch (error) {
        logger.error('[HeatmapPage] Error checking plan:', error);
        // On error, default to pro (allow access)
        setHasHeatmapAccess(true);
        setCurrentPlan('pro');
      }
    };
    
    // Check on mount
    checkPlanAccess();
    
    // Refetch plan when tab becomes visible (user might have changed plan in another tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPlanAccess();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also refetch every 30 seconds while on the page
    const interval = setInterval(checkPlanAccess, 30000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, []);

  // Get user's current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((s) => ({
          ...s,
          lat: String(position.coords.latitude),
          lng: String(position.coords.longitude),
        }));
        setLocationLoading(false);
      },
      (error) => {
        logger.error('Error getting location:', error);
        // Fallback to a default location (New Delhi, India)
        setForm((s) => ({
          ...s,
          lat: '28.6139',
          lng: '77.2090',
        }));
        setLocationLoading(false);
      }
    );
  };

  // Get coordinates from Google My Business
  const getGMBCoordinates = async () => {
    try {
      setLocationLoading(true);
      setError('');
      
      // Get selected business from localStorage
      const selectedBusiness = localStorage.getItem('selectedBusiness');
      if (!selectedBusiness) {
        logger.log('[HeatmapPage] No business selected in localStorage');
        throw new Error('No business selected');
      }

      const business = JSON.parse(selectedBusiness);
      logger.log('[HeatmapPage] Selected business from localStorage:', business);
      
      // Try multiple possible coordinate formats in the business object
      let lat = null;
      let lng = null;
      let businessName = business.title || business.name || '';
      
      // Format 1: latlng object with latitude/longitude
      if (business.latlng?.latitude && business.latlng?.longitude) {
        lat = business.latlng.latitude;
        lng = business.latlng.longitude;
        logger.log('[HeatmapPage] Found coordinates in latlng object:', { lat, lng });
      }
      // Format 2: Direct lat/lng properties
      else if (business.lat && business.lng) {
        lat = business.lat;
        lng = business.lng;
        logger.log('[HeatmapPage] Found coordinates in direct lat/lng:', { lat, lng });
      }
      // Format 3: latitude/longitude properties
      else if (business.latitude && business.longitude) {
        lat = business.latitude;
        lng = business.longitude;
        logger.log('[HeatmapPage] Found coordinates in latitude/longitude:', { lat, lng });
      }
      // Format 4: storefrontAddress with lat/lng
      else if (business.storefrontAddress?.latitude && business.storefrontAddress?.longitude) {
        lat = business.storefrontAddress.latitude;
        lng = business.storefrontAddress.longitude;
        logger.log('[HeatmapPage] Found coordinates in storefrontAddress:', { lat, lng });
      }
      
      // If we found coordinates, use them
      if (lat && lng && lat !== 0 && lng !== 0) {
        setForm((s) => ({
          ...s,
          lat: String(lat),
          lng: String(lng),
          businessName: businessName || s.businessName
        }));
        
        // Store full business data for keyword suggestions (same as CompetitorAnalysisModal)
        setBusinessData(business);
        logger.log('[HeatmapPage] Set business data for keyword suggestions:', business.name || business.title);
        
        setLocationLoading(false);
        logger.log('[HeatmapPage] Successfully set GMB coordinates:', { lat, lng, businessName });
        return;
      }

      logger.log('[HeatmapPage] No valid coordinates in cache, fetching from GMB API');
      
      // If not in cache, fetch from GMB API
      const gmbService = new GMBService();
      const accessToken = await gmbService.getAccessToken();
      
      if (!business.accountName && !business.account) {
        logger.log('[HeatmapPage] No account information found in business object');
        throw new Error('Business account information not found');
      }

      const accountName = business.accountName || business.account;
      logger.log('[HeatmapPage] Fetching locations for account:', accountName);
      
      // Fetch fresh location data
      const locations = await gmbService.getLocations(accessToken, accountName);
      logger.log('[HeatmapPage] Fetched locations from GMB API:', locations);
      
      const currentLocation = locations.find(loc => 
        loc.name === business.name || 
        loc.title === business.title
      ) || locations[0];
      
      if (currentLocation?.latlng?.latitude && currentLocation?.latlng?.longitude) {
        setForm((s) => ({
          ...s,
          lat: String(currentLocation.latlng.latitude),
          lng: String(currentLocation.latlng.longitude),
          businessName: currentLocation.title || currentLocation.name || s.businessName
        }));
        
        // Store full business data for keyword suggestions (same as CompetitorAnalysisModal)
        setBusinessData(currentLocation);
        logger.log('[HeatmapPage] Set business data from GMB API:', currentLocation.title || currentLocation.name);
        
        logger.log('[HeatmapPage] Successfully fetched GMB coordinates:', currentLocation.latlng);
      } else {
        logger.log('[HeatmapPage] No valid coordinates found in GMB API response');
        throw new Error('Location coordinates not found in Google My Business profile');
      }
      
      setLocationLoading(false);
    } catch (error) {
      logger.error('[HeatmapPage] Error fetching GMB coordinates:', error);
      setLocationLoading(false);
      throw error; // Re-throw to trigger fallback
    }
  };

  // Auto-load GMB location on component mount
  useEffect(() => {
    if (!form.lat && !form.lng && !locationLoading) {
      // Try GMB coordinates first, fallback to current location
      getGMBCoordinates().catch(() => {
        logger.log('[HeatmapPage] GMB coordinates not available, using device location');
        getCurrentLocation();
      });
    }
  }, []);

  // Fetch dynamic keywords when businessData is available (same as CompetitorAnalysisModal)
  useEffect(() => {
    const fetchDynamicKeywords = async () => {
      // Prevent duplicate fetches
      if (keywordsFetchedRef.current || !businessData) {
        return;
      }
      
      keywordsFetchedRef.current = true;

      try {
        setKeywordsLoading(true);
        logger.log('[HeatmapPage] Fetching keywords for business:', businessData.name || businessData.title);
        
        // Extract business category from GMB data
        const businessCategory = GoogleAdsKeywordService.extractBusinessCategory(businessData);
        
        logger.log('[HeatmapPage] Business category:', businessCategory);
        
        // Get real keywords from DataForSEO API (India only) - same as CompetitorAnalysisModal
        const keywords = await GoogleAdsKeywordService.getKeywordSuggestions({
          businessCategory,
          location: 'India'
        });
        
        // Extract search terms for selection
        const keywordTerms = keywords.slice(0, 6).map(k => k.searchTerm);
        
        logger.log('[HeatmapPage] Fetched keywords:', keywordTerms);
        
        if (keywordTerms.length > 0) {
          setKeywordSuggestions(keywordTerms);
          setShowKeywordSuggestions(true);
        } else {
          setKeywordSuggestions([]);
          setShowKeywordSuggestions(false);
        }
        
      } catch (error) {
        logger.error('[HeatmapPage] Error fetching dynamic keywords:', error);
        setKeywordSuggestions([]);
      } finally {
        setKeywordsLoading(false);
      }
    };

    fetchDynamicKeywords();
  }, [businessData]);

  // Handle keyword suggestion click
  const handleKeywordSuggestionClick = (keyword) => {
    setForm(prev => ({ ...prev, keyword }));
    setShowKeywordSuggestions(false);
  };

  // Prefill from query params and optionally auto-run
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const businessName = params.get('businessName') || '';
      const placeId = params.get('placeId') || '';
      const keyword = params.get('keyword') || '';

      if (businessName || keyword) {
        setForm((s) => ({
          ...s,
          businessName: businessName || s.businessName,
          keyword: keyword || s.keyword,
        }));
      }

      const resolveAndMaybeRun = async () => {
        try {
          if (placeId && (!form.lat || !form.lng)) {
            const coords = await places.getCoordinatesByPlaceId(placeId);
            setForm((s) => ({ ...s, lat: String(coords.lat), lng: String(coords.lng) }));
            // If keyword provided and not yet auto-run, trigger analysis
            if (keyword && !autoRunTried.current) {
              autoRunTried.current = true;
              // Slight delay to ensure state is set
              setTimeout(() => run(), 50);
            }
          } else if (keyword && businessName && form.lat && form.lng && !autoRunTried.current) {
            autoRunTried.current = true;
            setTimeout(() => run(), 0);
          }
        } catch (e) {
          setError(`Failed to resolve coordinates by Place ID: ${e?.message || e}`);
        }
      };

      resolveAndMaybeRun();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    } catch (_) {}
  }, [places]);

  

  const run = async () => {
    setError('');
    setResult(null);
    const center = { lat: Number(form.lat), lng: Number(form.lng) };
    const config = {
      businessName: form.businessName.trim(),
      businessAddress: '',
      keyword: form.keyword.trim(),
      centerLocation: center,
      gridSize: Number(form.gridSize),
      searchRadius: Number(form.radiusKm),
      googleApiKey: defaultApiKey,
    };
    const validation = rankingService.validateConfig(config);
    if (!validation.isValid) {
      setError(validation.errors.join(' \n'));
      return;
    }
    setRunning(true);
    try {
      const data = await rankingService.performRankingAnalysis(config, (pct, _total, msg) => setProgress({ pct, msg }));
      setResult(data);
    } catch (e) {
      setError(e?.message || 'Unexpected error while running analysis');
    } finally {
      setRunning(false);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          GMB Ranking Heatmap
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
         Enter business name, keyword, and center coordinates. Then Run Analysis to compute grid rankings.
        </Typography>

        {/* Plan Gating - Heatmap requires Advance or Pro plan */}
        {hasHeatmapAccess === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} />
            <Typography sx={{ ml: 2, color: '#6b7280' }}>Checking access...</Typography>
          </Box>
        ) : !hasHeatmapAccess ? (
          <Paper 
            sx={{ 
              p: 4, 
              textAlign: 'center', 
              bgcolor: '#FEF3C7', 
              border: '1px solid #F59E0B',
              borderRadius: 2 
            }}
          >
            <LockIcon sx={{ fontSize: 48, color: '#F59E0B', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Heatmap Analysis Requires Upgrade
            </Typography>
            <Typography variant="body2" sx={{ color: '#78350F', mb: 2 }}>
              Heatmap Analysis is available on <strong>Advance</strong> and <strong>Pro</strong> plans.
              {currentPlan !== 'none' && (
                <span> Your current plan is <strong style={{ textTransform: 'capitalize' }}>{currentPlan}</strong>.</span>
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: '#92400E', mb: 3 }}>
              Upgrade your plan to access visual ranking heatmaps and grid analysis.
            </Typography>
            <Button 
              variant="contained" 
              sx={{ 
                bgcolor: '#F59E0B', 
                '&:hover': { bgcolor: '#D97706' },
                fontWeight: 600,
                px: 4
              }}
              onClick={() => window.location.href = '/pricing'}
            >
              Upgrade Now
            </Button>
          </Paper>
        ) : (
          <>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="Business Name" fullWidth value={form.businessName} onChange={update('businessName')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                label="Search Keyword" 
                fullWidth 
                value={form.keyword} 
                onChange={update('keyword')}
                onFocus={() => setShowKeywordSuggestions(keywordSuggestions.length > 0)}
                placeholder="Enter keyword or select from suggestions below"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Latitude" fullWidth value={form.lat} onChange={update('lat')} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Longitude" fullWidth value={form.lng} onChange={update('lng')} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button 
                variant="outlined" 
                fullWidth 
                onClick={getCurrentLocation}
                disabled={locationLoading}
                startIcon={<MyLocationIcon />}
                sx={{ height: '56px' }}
              >
                {locationLoading ? 'Loading...' : 'Use My Location'}
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField type="number" label="Pins (grid size)" fullWidth value={form.gridSize} onChange={update('gridSize')} />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField type="number" label="Radius (km)" fullWidth value={form.radiusKm} onChange={update('radiusKm')} />
            </Grid>
            
            {/* Keyword Suggestions Section */}
            {(showKeywordSuggestions || keywordsLoading) && (
              <Grid item xs={12}>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#0B91D6', fontWeight: 600 }}>
                    💡 Suggested Keywords for "{businessData ? GoogleAdsKeywordService.extractBusinessCategory(businessData) : form.businessName}"
                  </Typography>
                  {keywordsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="textSecondary">
                        Generating category-specific keywords...
                      </Typography>
                    </Box>
                  ) : (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {keywordSuggestions.map((keyword, index) => (
                        <Chip
                          key={index}
                          label={keyword}
                          onClick={() => handleKeywordSuggestionClick(keyword)}
                          sx={{
                            mb: 1,
                            cursor: 'pointer',
                            backgroundColor: form.keyword === keyword ? '#0B91D6' : '#f3f4f6',
                            color: form.keyword === keyword ? 'white' : '#374151',
                            '&:hover': {
                              backgroundColor: form.keyword === keyword ? '#0980C2' : '#e5e7eb'
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                  <Divider sx={{ mt: 2 }} />
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 1, 
                  height: 400, 
                  display: 'flex', 
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                <HeatmapPreviewMap
                  center={{ lat: Number(form.lat || 0), lng: Number(form.lng || 0) }}
                  gridSize={Number(form.gridSize) || 5}
                  radiusKm={Number(form.radiusKm) || 2}
                  height="100%"
                />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" sx={{ backgroundColor: '#0B91D6' }} onClick={run} disabled={running}>
                  {running ? 'Running…' : 'Continue'}
                </Button>
              </Stack>
            </Grid>
            {running && (
              <Grid item xs={12}>
                <LinearProgress variant="determinate" value={progress.pct} />
                <Typography variant="caption" sx={{ color: '#6b7280' }}>{progress.msg}</Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {result && (
          <>
            {/* Summary Stats Row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {(() => {
                const ranks = result.results.map(r => r.targetBusinessRank);
                const good = ranks.filter(r => typeof r === 'number' && r <= 3).length;
                const avg = ranks.filter(r => typeof r === 'number' && r > 3 && r <= 7).length;
                const poor = ranks.filter(r => r == null || r > 7).length;
                const total = ranks.length || 1;
                const avgRank = result.averageRank ?? null;
                const shareOfVoice = Math.round((good / total) * 100);
                const outOfTop20 = ranks.filter(r => r == null || r > 20).length;
                const cards = [
                  { label: 'Good', value: `${Math.round((good/total)*100)}%` },
                  { label: 'Poor', value: `${Math.round((poor/total)*100)}%` },
                  { label: 'Average', value: `${Math.round((avg/total)*100)}%` },
                  { label: 'Average Rank', value: avgRank == null ? '—' : Math.round(avgRank) },
                  { label: 'Share of voice', value: `${shareOfVoice}%` },
                  { label: 'Out of Top 20', value: `${Math.round((outOfTop20/total)*100)}%` },
                ];
                return cards.map((c, idx) => (
                  <Grid key={idx} item xs={6} sm={4} md={2}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                      <Typography sx={{ fontSize: 12, color: '#6b7280' }}>{c.label}</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 20 }}>{c.value}</Typography>
                    </Paper>
                  </Grid>
                ));
              })()}
            </Grid>

            {/* Map with ranked markers */}
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                mb: 2,
                height: 580,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>Map</Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <HeatmapMap center={result.centerLocation} results={result.results} height="100%" />
              </Box>
            </Paper>
          </>
        )}
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default HeatmapPage;
